import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import style from './AddToCartButton.module.scss'

// Placement drives both the label and the size: grid cards say "Add to Cart" on the full pill,
// the banner and the carousels say "Add" on a smaller one, cart lines get the white stepper.
const PLACEMENTS = {
  grid: { label: 'addToCart', className: '' },
  banner: { label: 'add', className: style.banner },
  cart: { label: 'addToCart', className: style.cart }
} as const

interface IAddToCartButtonProps {
  amount: number
  findability: boolean
  limit: number
  disabled?: boolean
  placement?: keyof typeof PLACEMENTS
  spread?: boolean
  // A product with variants offers "View" (open the page and pick one) in place of Add.
  view?: boolean
  onView?: () => void
  onAdd: (qnt: number) => void
}

const AddToCartButton = ({
  amount,
  findability,
  limit,
  disabled = false,
  placement = 'grid',
  spread = false,
  view = false,
  onView,
  onAdd
}: IAddToCartButtonProps) => {
  const { t } = useSurveyLocale()
  const viewport = useViewport()
  const atCap = !findability && limit > 0 && amount >= limit
  const modifiers = `${PLACEMENTS[placement].className} ${spread ? style.spread : ''} ${viewport.mobile ? style.mobile : ''}`

  if (view) {
    return (
      <button
        type="button"
        data-testid="SW-EC-ViewButton"
        className={`${style.pill} ${modifiers}`}
        disabled={disabled}
        onClick={event => {
          event.stopPropagation()
          onView?.()
        }}
      >
        {t('questions', 'ec', 'samsclub', 'view')}
      </button>
    )
  }

  if (findability || amount <= 0) {
    return (
      <button
        type="button"
        data-testid="SW-EC-AddToCartButton"
        className={`${style.pill} ${modifiers}`}
        disabled={disabled}
        onClick={event => {
          event.stopPropagation()
          onAdd(1)
        }}
      >
        {t('questions', 'ec', 'samsclub', PLACEMENTS[placement].label)}
      </button>
    )
  }

  return (
    <div
      data-testid="SW-EC-AddToCartButton"
      className={`${style.stepper} ${modifiers}`}
      onClick={event => event.stopPropagation()}
    >
      <button
        type="button"
        className={style.step}
        onClick={() => onAdd(-1)}
        disabled={disabled}
        aria-label={t('questions', 'ec', 'samsclub', amount === 1 ? 'remove' : 'decreaseQuantity')}
      >
        <i className="pi pi-minus" />
      </button>
      <span className={style.qty}>{amount}</span>
      <button
        type="button"
        className={style.step}
        onClick={() => onAdd(1)}
        disabled={atCap || disabled}
        aria-label={t('questions', 'ec', 'samsclub', 'increaseQuantity')}
      >
        <i className="pi pi-plus" />
      </button>
    </div>
  )
}

export default AddToCartButton
